import React from 'react';
import { Box, Chip, Divider, Paper } from '@mui/material';
import * as Yup from 'yup';
import { useResizeObserver, useScrollableView } from '../../Layout';
import { useAddMessagingToDoMutation, useCompleteMessagingToDoMutation, useConversationToDosQuery, useMarkMessagesAsReadMutation, useUserMessagesQuery } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { Color } from '../types';
import { MessageBubble, MessageBubbleActionItem, MessageItem, MessagingBubbleDirection } from './MessageBubble.component';
import { Loader } from '../../Loader';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { Message, MessagingToDo } from '@uplift/api';
import { useMessagingContext } from '../MessagingProvider.component';
import debounce from 'lodash/debounce';
import { CancelButton, FormModal, SubmitButton } from '../../Form';
import { ModalActions, ModalContent, ModalTitle } from '../../Modal';
import sortBy from 'lodash/sortBy';
import { Formik } from 'formik';
import { TextInput } from '../../Inputs';
import { useAlert } from '../../AlertProvider';
import { UserRole } from '@uplift/types';
import { ArrowForwardIosRounded, CheckBoxOutlineBlank, CheckBoxOutlined } from '@mui/icons-material';
import { useThrottleValue } from '@uplift/hooks';
import { ProgressIcon } from '../../Icons';


type MessageListItem = {
  type: 'message'; datetime: string; message: Message; viewed: boolean;
} | {
  type: 'todo'; datetime: string; todo: MessagingToDo; viewed: boolean;
};


export type MessagesContainerProps = {
  conversationId: string;
  color: Color;
  includeToDos?: boolean;
};


const AddToDoModal: React.VFC<{ message: Message; onClose: () => void }> = (props) => {
  const me = useCurrentuser();
  const alert = useAlert();
  const addMessagingToDoMutation = useAddMessagingToDoMutation();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        messageId: props.message.id,
        description: '',
      }}
      onSubmit={async (values) => {
        await addMessagingToDoMutation.mutateAsync({ messageId: props.message.id, description: values.description });
        alert.success('To-Do added successfully');
        props.onClose();
      }}
      validationSchema={Yup.object().shape({
        messageId: Yup.string().required(),
        description: Yup.string().required('Description is required'),
      })}
    >
      <FormModal open onClose={() => props.onClose()} maxWidth="md" fullWidth>
        <ModalTitle>Add To-Do</ModalTitle>
        <ModalContent>
          <TextInput name="description" placeholder="Description" color="secondary" multiline minRows={3} autoFocus />
          <Box pt={2} display="flex">
            <MessageBubble
              message={props.message}
              direction={me.id === props.message.senderId ? 'send' : 'receive'}
              downloadableAttachments
              color="secondary"
            />
          </Box>
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={props.onClose}>Close</CancelButton>
          <SubmitButton color="secondary">Add</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};


const MessagesListItem: React.FC<{
  message: Message;
  direction: MessagingBubbleDirection;
  color: Color;
  markAsRead?: boolean;
  downloadableAttachments?: boolean;
  setAddToDoModal: (message: Message) => void;
}> = ({ markAsRead = true, downloadableAttachments = true, ...props }) => {
  const me = useCurrentuser();
  const markAsReadMutation = useMarkMessagesAsReadMutation();
  const messagingContext = useMessagingContext();

  const handleMarkAsRead = React.useMemo(() => debounce(() => {
    if (markAsRead) {
      markAsReadMutation.mutate({ messageId: props.message.id });
    }
  }, 1000), [ props.message.id, markAsRead ]);
  const handleMarkAsReadCancel = () => handleMarkAsRead.cancel();

  // Call the cancel function when the component unmounts
  React.useEffect(() => () => handleMarkAsReadCancel(), []);

  return (
    <MessageItem
      message={props.message}
      downloadableAttachments={downloadableAttachments}
      direction={props.direction}
      color={props.color}
      highlighted={messagingContext.focusedMessageId === props.message.id}
      onAppearOnScreen={() => {
        if (props.message.seenAt || props.direction === 'send') return;
        handleMarkAsRead();
      }}
      onDisappearOnScreen={() => {
        handleMarkAsReadCancel();
      }}
      actions={me.role === UserRole.provider ? (
        <MessageBubbleActionItem onClick={() => props.setAddToDoModal(props.message)}>
          Mark as To Do
        </MessageBubbleActionItem>
      ) : undefined}
    />
  );
};

const ToDoListItem: React.FC<{ todo: MessagingToDo }> = (props) => {
  const messagingContext = useMessagingContext();
  const completeMessagingToDoMutation = useCompleteMessagingToDoMutation();
  const isLoading = useThrottleValue(completeMessagingToDoMutation.isLoading, 1000);

  const checkbox = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
          }}
        >
          <ProgressIcon size={16} sx={{ color: 'secondary.main' }} />
        </Box>
      );
    }

    if (props.todo.status === 'completed') {
      return (
        <CheckBoxOutlined
          sx={{ color: 'secondary.main', cursor: 'pointer', }}
          onClick={(e) => {
            e.stopPropagation();
            completeMessagingToDoMutation.mutate({ todoId: props.todo.id, complete: false });
          }}
        />
      );
    }

    if (props.todo.status === 'archived') {
      return (
        <CheckBoxOutlineBlank sx={{ color: 'secondary.main' }} />
      );
    }

    if (props.todo.status === 'active') {
      return (
        <CheckBoxOutlineBlank
          sx={{ color: 'secondary.main', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            completeMessagingToDoMutation.mutate({ todoId: props.todo.id, complete: true });
          }}
        />
      );
    }
  }, [ props.todo.id, props.todo.status, isLoading ]);

  const color = {
    completed: 'secondary' as const,
    archived: 'secondary' as const,
    active: 'secondary' as const,
  }[props.todo.status];

  return (
    <Box
      sx={{
        p: 0.5,
        display: 'flex',
        justifyContent: 'center',
        userSelect: 'none',
        cursor: 'pointer',
      }}
      onClick={() => {
        messagingContext.openToDo(props.todo.id);
      }}
    >
      <Box
        component={Paper}
        variant="outlined"
        sx={{
          maxWidth: '250px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          p: 1,
          gap: 1,
          bgcolor: `${color}.background`,
          borderColor: `${color}.border`,
        }}
      >
        {checkbox}
        <Box
          component="span"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: props.todo.status === 'archived' ? 'line-through' : 'none',
          }}
        >
          To-do: {props.todo.description}
        </Box>
        <ArrowForwardIosRounded color={color} sx={{ fontSize: 14, mr: 0.5 }} />
      </Box>
    </Box>
  );
};


const MessagesGroup: React.FC<{
  color: Color;
  group: { date: string; items: MessageListItem[] };
  setAddToDoModal: (message: Message) => void;
}> = ({ color, group, setAddToDoModal }) => {
  const me = useCurrentuser();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const groupLabel = React.useMemo(() => {
    const date = CalendarDateService.date(group.date, DateFormats.ISO_DATE);

    if (date.isSame(CalendarDateService.date(), 'date')) {
      return 'Today';
    }

    return CalendarDateService.format(date, 'dddd, MMMM D', 'dddd, MMMM D, YYYY');
  }, [ group.date ]);

  return (
    <Box
      ref={rootRef}
      position="relative"
      data-testid="messages-group"
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          top: 0,
          height: '51px',
          bgcolor: group.items[0]?.viewed ? 'transparent' : 'grey.50',
        }}
      >
        <Divider
          sx={{
            position: 'absolute',
            top: '25px',
            width: '100%',
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '51px',
          zIndex: 1,
        }}
      >
        <Chip
          sx={{
            bgcolor: 'white',
            cursor: 'pointer',

            '&.MuiChip-root:hover': {
              bgcolor: 'white',
            },
          }}
          variant="outlined"
          label={groupLabel}
          data-testid="messages-group-title"
          onClick={() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      </Box>
      {group.items.map((item) => {
        if (item.type === 'message') {
          return (
            <MessagesListItem
              key={item.message.id}
              message={item.message}
              color={color}
              direction={item.message.senderId === me.id ? 'send' : 'receive'}
              setAddToDoModal={setAddToDoModal}
            />
          );
        }

        if (item.type === 'todo') {
          return (
            <ToDoListItem
              key={item.todo.id}
              todo={item.todo}
            />
          );
        }

        return null;
      })}
    </Box>
  );
};


export const MessagesContainer: React.FC<MessagesContainerProps> = ({ conversationId, color, includeToDos = false }) => {
  const me = useCurrentuser();
  const messagingContext = useMessagingContext();
  const scrollableView = useScrollableView();
  const [ sticked, setSticked ] = React.useState(true);
  const scrollContainer = scrollableView.scrollContainer;
  const [ body, setBody ] = React.useState<HTMLDivElement | null>(null);
  const { data: messages = [], isLoading, isFetchedAfterMount } = useUserMessagesQuery(conversationId, { suspense: true });
  const { data: todos = [] } = useConversationToDosQuery(conversationId, includeToDos);
  const lastMessage = messages[messages.length - 1];

  const messagesAndToDos: MessageListItem[] = React.useMemo(() => {
    const messageObjs = messages.map(m => ({ type: 'message' as const, datetime: m.sentAt, message: m, viewed: !!m.seenAt }));
    const todosObjs = todos.map(t => ({ type: 'todo' as const, datetime: t.createdAt, todo: t, viewed: true }));

    return sortBy([ ...messageObjs, ...todosObjs ], 'datetime');
  }, [ messages, todos ]);

  const [ addToDoModalMessage, setAddToDoModalMessage ] = React.useState<Message | null>(null);

  React.useEffect(() => {
    if (!scrollContainer || !isFetchedAfterMount) return;
    const oldestUnreadMessage = messages.find((message) => !message.seenAt && message.senderId !== me.id);

    if (!oldestUnreadMessage) return;
    scrollContainer.querySelector(`[data-message-id="${oldestUnreadMessage.id}"]`)?.scrollIntoView({ block: 'start' });
  }, [ isFetchedAfterMount ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer || lastMessage?.senderId !== me.id) return;

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, [ lastMessage?.message ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer) return;

    const scrollHandler = () => {
      if (!scrollContainer) return;
      if (scrollContainer.clientHeight === Math.floor(scrollContainer.scrollHeight - scrollContainer.scrollTop)) {
        setSticked(true);
      } else {
        setSticked(false);
      }
    };

    scrollContainer.addEventListener('scroll', scrollHandler);

    return () => scrollContainer.removeEventListener('scroll', scrollHandler);
  }, [ scrollContainer ]);

  React.useEffect(() => {
    if (
      !messagingContext.focusedMessageId
      || !scrollContainer
    ) {
      return;
    }


    const timeoutId = setTimeout(() => {
      scrollContainer.querySelector(`[data-message-id="${messagingContext.focusedMessageId}"]`)?.scrollIntoView({ block: 'start', behavior: 'instant' });
      messagingContext.setFocusedMessageId(undefined);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [ messagingContext.focusedMessageId, scrollContainer ]); // eslint-disable-line react-hooks/exhaustive-deps

  useResizeObserver(body, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  useResizeObserver(scrollContainer, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  const timezone = useTimezone();

  const groups = React.useMemo(() => {
    return messagesAndToDos.reduce((acc, item) => {
      const date = CalendarDateService.date(item.datetime).tz(timezone).format(DateFormats.ISO_DATE);
      const latestGroup = acc[acc.length - 1];

      if (latestGroup && latestGroup.date === date) {
        latestGroup.items.push(item);
      } else {
        const newGroup = { date: date, items: [ item ] };

        acc.push(newGroup);
      }

      return acc;
    }, [] as { date: string; items: MessageListItem[] }[]);
  }, [ messagesAndToDos, timezone ]);

  return (
    <Box
      component="div"
      key={conversationId}
      ref={setBody}
      sx={{
        gap: 0,
        py: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {groups.map(group => (
        <MessagesGroup
          key={group.date}
          color={color}
          group={group}
          setAddToDoModal={setAddToDoModalMessage}
        />
      ))}
      {isLoading && (
        <Loader color={color} />
      )}
      {addToDoModalMessage && (
        <AddToDoModal
          message={addToDoModalMessage}
          onClose={() => setAddToDoModalMessage(null)}
        />
      )}
    </Box>
  );
};
